import { useState } from 'react';
import type { ChangeEvent } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { downloadOrderNoTemplate, importOrderNos, parseOrderNoSheet } from '@/api/member';
import type { ImportOrderNosResp } from '@shared/api.interface';
import { ORDER_NO_IMPORT_CHUNK_SIZE } from '@shared/api.interface';
import { extractApiErrorMessage } from './api-error';
import { columnLabel, guessColumns } from './redeem-batch-file';

interface ImportOrderNosDialogProps {
  open: boolean;
  onClose: () => void;
}

type Sheet = { fileName: string; header: string[]; rows: string[][] };

const emptyResult = (): ImportOrderNosResp => ({ matched: 0, unchanged: 0, conflicts: [], not_found: [], invalid_rows: 0 });

export function ImportOrderNosDialog({ open, onClose }: ImportOrderNosDialogProps) {
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [orderColumn, setOrderColumn] = useState(-1);
  const [codeColumn, setCodeColumn] = useState(-1);
  const [preview, setPreview] = useState<ImportOrderNosResp | null>(null);
  const [isWorking, setIsWorking] = useState(false);
  const [fileError, setFileError] = useState('');
  const [isReading, setIsReading] = useState(false);

  const handleDownloadTemplate = async () => {
    try {
      const blob = await downloadOrderNoTemplate();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = '发货记录导入模板.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      toast.error(extractApiErrorMessage(error, '下载模板失败'));
    }
  };

  const reset = () => {
    setSheet(null);
    setPreview(null);
    setFileError('');
    setOrderColumn(-1);
    setCodeColumn(-1);
  };

  const close = () => {
    reset();
    onClose();
  };

  const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    reset();
    setIsReading(true);
    try {
      // 表格在服务端解析（大文件、GBK 编码、长订单号都在那里处理）
      const parsed = await parseOrderNoSheet(file);
      const guess = guessColumns(parsed.header);
      setSheet({ fileName: parsed.file_name || file.name, header: parsed.header, rows: parsed.rows });
      setOrderColumn(guess.orderColumn);
      setCodeColumn(guess.codeColumn);
    } catch (error) {
      setFileError(extractApiErrorMessage(error, '读取文件失败，请确认是 CSV 或 Excel 文件'));
    } finally {
      setIsReading(false);
    }
  };

  const buildRows = () =>
    sheet!.rows.map(row => ({ code: row[codeColumn] ?? '', order_no: row[orderColumn] ?? '' }));

  // 请求体有大小限制，按块发送并合并结果
  const send = async (dryRun: boolean) => {
    const rows = buildRows();
    const total = emptyResult();
    for (let start = 0; start < rows.length; start += ORDER_NO_IMPORT_CHUNK_SIZE) {
      const result = await importOrderNos({ rows: rows.slice(start, start + ORDER_NO_IMPORT_CHUNK_SIZE), dry_run: dryRun });
      total.matched += result.matched;
      total.unchanged += result.unchanged;
      total.invalid_rows += result.invalid_rows;
      total.conflicts.push(...result.conflicts);
      total.not_found.push(...result.not_found);
    }
    return total;
  };

  const handlePreview = async () => {
    if (!sheet || orderColumn < 0 || codeColumn < 0 || orderColumn === codeColumn) return;
    setIsWorking(true);
    try {
      setPreview(await send(true));
    } catch (error) {
      toast.error(extractApiErrorMessage(error, '预览失败'));
    } finally {
      setIsWorking(false);
    }
  };

  const handleCommit = async () => {
    if (!preview || isWorking) return;
    setIsWorking(true);
    try {
      const result = await send(false);
      toast.success(`已写入 ${result.matched} 个订单号`);
      close();
    } catch (error) {
      toast.error(extractApiErrorMessage(error, '导入失败'));
    } finally {
      setIsWorking(false);
    }
  };

  const skipped = preview ? [
    ...preview.conflicts.map(item => ({ code: item.code, reason: `已有订单号 ${item.existing_order_no}，表格里是 ${item.order_no}，保持原样` })),
    ...preview.not_found.map(code => ({ code, reason: '系统里找不到这个码' })),
  ] : [];

  const columnSelect = (id: string, label: string, value: number, onChange: (value: number) => void) => (
    <div className="min-w-0 flex-1 space-y-1.5">
      <label className="text-sm font-medium" htmlFor={id}>{label}</label>
      <Select value={value >= 0 ? String(value) : ''} onValueChange={(next) => { onChange(Number(next)); setPreview(null); }}>
        <SelectTrigger id={id} className="h-11"><SelectValue placeholder="请选择" /></SelectTrigger>
        <SelectContent>
          {sheet!.header.map((_, index) => (
            <SelectItem key={index} value={String(index)}>{columnLabel(index, sheet!.header)}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className="max-h-[90dvh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>导入发货记录</DialogTitle>
          <DialogDescription>
            从发货软件导出“订单号 + 卡密”的表格，导入后自动把订单号写到对应的码上。以后用户报订单号，就能在会员管理里直接搜到码。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-background px-3 py-2.5 text-sm">
            <span className="text-muted-foreground">没有现成表格？下载模板，填好“订单编号”和“卡密”两列再上传。</span>
            <Button variant="outline" size="sm" className="bg-white" onClick={() => void handleDownloadTemplate()}>
              <Download className="mr-1 h-4 w-4" />
              下载模板
            </Button>
          </div>
          <label className="block cursor-pointer rounded-xl border border-dashed bg-background px-4 py-5 text-center text-sm text-muted-foreground hover:bg-accent">
            <input type="file" accept=".csv,.txt,.xlsx,.xls" className="sr-only" onChange={handleFile} disabled={isReading} />
            {isReading ? '正在读取…' : sheet
              ? <>已选择：<strong className="text-foreground">{sheet.fileName}</strong>（共 {sheet.rows.length} 行）· 点击重新选择</>
              : '点击选择文件（支持 CSV / Excel，第一行是表头）'}
          </label>
          {fileError && <p role="alert" className="text-sm text-destructive">{fileError}</p>}

          {sheet && (
            <div className="flex flex-col gap-3 sm:flex-row">
              {columnSelect('import-order-column', '哪一列是订单号', orderColumn, setOrderColumn)}
              {columnSelect('import-code-column', '哪一列是卡密', codeColumn, setCodeColumn)}
            </div>
          )}
          {sheet && orderColumn >= 0 && orderColumn === codeColumn && (
            <p className="text-sm text-destructive">订单号和卡密不能选同一列</p>
          )}

          {preview && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 tabular-nums sm:grid-cols-3">
                <div className="rounded-xl bg-background px-3 py-2.5">
                  <b className="block text-xl text-emerald-700">{preview.matched}</b>
                  <span className="text-xs text-muted-foreground">找到对应的码，会写入订单号</span>
                </div>
                <div className="rounded-xl bg-background px-3 py-2.5">
                  <b className="block text-xl text-amber-700">{preview.conflicts.length + preview.unchanged}</b>
                  <span className="text-xs text-muted-foreground">已有订单号，保持原样不覆盖</span>
                </div>
                <div className="rounded-xl bg-background px-3 py-2.5">
                  <b className="block text-xl text-red-700">{preview.not_found.length + preview.invalid_rows}</b>
                  <span className="text-xs text-muted-foreground">找不到这个码或格式不对</span>
                </div>
              </div>
              {skipped.length > 0 && (
                <details className="text-sm">
                  <summary className="cursor-pointer">查看 {skipped.length} 条不会写入的记录</summary>
                  <ul className="mt-2 max-h-48 divide-y overflow-y-auto rounded-xl border">
                    {skipped.map((item, index) => (
                      <li key={`${item.code}-${index}`} className="flex flex-wrap justify-between gap-2 px-3 py-2">
                        <span className="font-mono">{item.code}</span>
                        <span className="text-muted-foreground">{item.reason}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {preview.invalid_rows > 0 && (
                <p className="text-xs text-muted-foreground">另有 {preview.invalid_rows} 行的卡密或订单号是空的、或含有不支持的字符，已跳过。</p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close}>取消</Button>
          {preview ? (
            <Button onClick={handleCommit} disabled={isWorking || preview.matched === 0} className="bg-cta text-cta-foreground hover:bg-cta-hover">
              {isWorking && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              写入 {preview.matched} 个订单号
            </Button>
          ) : (
            <Button
              onClick={handlePreview}
              disabled={!sheet || orderColumn < 0 || codeColumn < 0 || orderColumn === codeColumn || isWorking}
            >
              {isWorking && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              预览
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
